import { Box, TextField, Typography } from "@mui/material";
import validateStartTime from "../../day/validation/validateStartTime";
import validateEndTime from "../../day/validation/validateEndTime";

/**
 * Reusable time range input fields for block editing/creation
 *
 * @param {Object} props
 * @param {string} props.startTime - Current start time value
 * @param {string} props.endTime - Current end time value
 * @param {function} props.onStartTimeChange - Callback when start time changes
 * @param {function} props.onEndTimeChange - Callback when end time changes
 * @param {boolean} [props.showValidation=true] - Whether to show validation errors
 * @param {boolean} [props.startTouched] - Whether start field has been touched (for delayed validation)
 * @param {boolean} [props.endTouched] - Whether end field has been touched (for delayed validation)
 * @param {function} [props.onStartBlur] - Callback when start time field loses focus
 * @param {function} [props.onEndBlur] - Callback when end time field loses focus
 * @param {number} [props.gap=2] - Gap between fields
 */
const TimeRangeFields = ({
  startTime,
  endTime,
  onStartTimeChange,
  onEndTimeChange,
  showValidation = true,
  startTouched = true,
  endTouched = true,
  onStartBlur,
  onEndBlur,
  gap = 2,
}) => {
  const startTimeError = validateStartTime(startTime, endTime);
  const endTimeError = validateEndTime(endTime, startTime);

  const showStartError = showValidation && startTouched && !!startTimeError;
  const showEndError = showValidation && endTouched && !!endTimeError;

  return (
    <>
      <Box sx={{ display: "flex", gap }}>
        <TextField
          label="Start Time"
          value={startTime}
          onChange={(e) => onStartTimeChange(e.target.value)}
          error={showStartError}
          helperText={showStartError ? startTimeError : ""}
          onBlur={onStartBlur}
          size="small"
          sx={{ flex: 1, mb: 0 }}
        />
        <TextField
          label="End Time"
          value={endTime}
          onChange={(e) => onEndTimeChange(e.target.value)}
          error={showEndError}
          helperText={showEndError ? endTimeError : ""}
          onBlur={onEndBlur}
          size="small"
          sx={{ flex: 1, mb: 0 }}
        />
      </Box>
      {startTime >= endTime && !startTimeError && !endTimeError && (
        <Typography variant="caption" color="error">
          End time must be after start time
        </Typography>
      )}
    </>
  );
};

/**
 * Utility to validate time range
 * @param {string} startTime
 * @param {string} endTime
 * @returns {boolean} Whether the time range is valid
 */
export const isTimeRangeValid = (startTime, endTime) => {
  const startError = validateStartTime(startTime, endTime);
  const endError = validateEndTime(endTime, startTime);
  return !startError && !endError;
};

export default TimeRangeFields;
